export const deg2rad = (d) => (d * Math.PI) / 180;

export function rotatePt([x, y], deg) {
  const r = deg2rad(deg), c = Math.cos(r), s = Math.sin(r);
  return [x * c - y * s, x * s + y * c];
}

export function worldPoints(piece) {
  return piece.points.map((p) => {
    const [rx, ry] = rotatePt(p, piece.rotation);
    return [rx + piece.x, ry + piece.y];
  });
}

export function worldHoles(piece) {
  return (piece.holes || []).map((h) => worldPoints({ ...piece, points: h }));
}

// outline + cut-outs as one even-odd path (holes render as see-through)
export const pieceD = (outline, holes = []) => pathD(outline) + holes.map((h) => pathD(h)).join("");

export function toLocal(piece, [wx, wy]) {
  return rotatePt([wx - piece.x, wy - piece.y], -piece.rotation);
}

export function bbox(pts) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of pts) {
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
}

// Every loop of a piece (outline + cut-outs). Multi-part decals keep their other letters / marks as extra loops, so
// sizes and layout must look at all of them, not just the main outline.
export const loops = (p) => [...p.points, ...(p.holes || []).flat()];
// Axis-aligned frame around every loop — what a multi-part decal occupies on the film
export const loopRect = (pts) => { const b = bbox(pts); return [[b.minX, b.minY], [b.maxX, b.minY], [b.maxX, b.maxY], [b.minX, b.maxY]]; };

export function signedArea(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i], [x2, y2] = pts[(i + 1) % pts.length];
    a += x1 * y2 - x2 * y1;
  }
  return a / 2;
}
export const area = (pts) => Math.abs(signedArea(pts));

const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

export function segIntersect(a, b, c, d) {
  const d1 = cross(c, d, a), d2 = cross(c, d, b), d3 = cross(a, b, c), d4 = cross(a, b, d);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

export function pointInPoly([x, y], poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function polysIntersect(a, b) {
  const ba = bbox(a), bb = bbox(b);
  if (ba.maxX < bb.minX || bb.maxX < ba.minX || ba.maxY < bb.minY || bb.maxY < ba.minY) return false;
  for (let i = 0; i < a.length; i++) {
    const a1 = a[i], a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      if (segIntersect(a1, a2, b[j], b[(j + 1) % b.length])) return true;
    }
  }
  return pointInPoly(a[0], b) || pointInPoly(b[0], a);
}

export function selfIntersects(pts) {
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;
      if (segIntersect(pts[i], pts[(i + 1) % n], pts[j], pts[(j + 1) % n])) return true;
    }
  }
  return false;
}

const segPoint = (a, b, c, d) => {
  const den = (b[0] - a[0]) * (d[1] - c[1]) - (b[1] - a[1]) * (d[0] - c[0]);
  if (Math.abs(den) < 1e-12) return null;
  const t = ((c[0] - a[0]) * (d[1] - c[1]) - (c[1] - a[1]) * (d[0] - c[0])) / den;
  return [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])];
};

/** Untangle a self-intersecting outline: at every crossing the smaller of the two loops (a hairpin left by an offset, a
 *  rounding or a hand edit) is cut away and replaced by the crossing point, until the outline is simple again.
 *  Returns null when the result would lose more than a third of the area — that is a real problem, not a loop. */
export function untangle(pts) {
  const b = bbox(pts);
  const area0 = Math.max(Math.abs(signedArea(pts)), 0.5 * b.w * b.h);  // a bow-tie's signed halves cancel: judge against the footprint too
  let cur = pts.map((p) => [p[0], p[1]]);
  for (let guard = 0; guard < 60; guard++) {
    const n = cur.length;
    let hit = null;
    for (let i = 0; i < n && !hit; i++) {
      for (let j = i + 2; j < n; j++) {
        if (i === 0 && j === n - 1) continue;
        if (segIntersect(cur[i], cur[(i + 1) % n], cur[j], cur[(j + 1) % n])) { hit = [i, j]; break; }
      }
    }
    if (!hit) break;
    const [i, j] = hit, x = segPoint(cur[i], cur[(i + 1) % n], cur[j], cur[(j + 1) % n]);
    if (!x) return null;
    const loop = [x, ...cur.slice(i + 1, j + 1)];           // the part between the two crossing segments
    const rest = [...cur.slice(0, i + 1), x, ...cur.slice(j + 1)];  // the outline with that part removed
    cur = Math.abs(signedArea(rest)) >= Math.abs(signedArea(loop)) ? rest : loop;
    if (cur.length < 3) return null;
  }
  if (selfIntersects(cur) || Math.abs(signedArea(cur)) < 0.67 * area0) return null;
  return cur;
}

const norm = ([x, y]) => { const l = Math.hypot(x, y) || 1; return [x / l, y / l]; };

export function offsetPolygon(pts, d) {
  if (!d) return pts.map((p) => [p[0], p[1]]);
  const n = pts.length, sign = signedArea(pts) > 0 ? 1 : -1, out = [];
  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n], cur = pts[i], next = pts[(i + 1) % n];
    const e1 = [cur[0] - prev[0], cur[1] - prev[1]], e2 = [next[0] - cur[0], next[1] - cur[1]];
    const n1 = norm(sign > 0 ? [e1[1], -e1[0]] : [-e1[1], e1[0]]);
    const n2 = norm(sign > 0 ? [e2[1], -e2[0]] : [-e2[1], e2[0]]);
    let bis = [n1[0] + n2[0], n1[1] + n2[1]];
    bis = Math.hypot(bis[0], bis[1]) < 1e-6 ? n1 : norm(bis);
    const k = d / Math.max(0.35, bis[0] * n1[0] + bis[1] * n1[1]);
    out.push([cur[0] + bis[0] * k, cur[1] + bis[1] * k]);
  }
  return out;
}

export const snapVal = (v, g) => (g ? Math.round(v / g) * g : v);

// Shadow Cut: a weeding contour exactly `d` outside the outline. Every edge is pushed out by d; convex corners are joined by an
// arc (a true parallel line, no mitre spikes) and concave corners meet where the two pushed edges cross.
export function shadowContour(pts, d) {
  const n = pts.length;
  if (n < 3 || !(d > 0)) return pts.map((p) => [p[0], p[1]]);
  const sgn = signedArea(pts) > 0 ? 1 : -1;
  const nrm = (a, b) => { const e = [b[0] - a[0], b[1] - a[1]]; return norm(sgn > 0 ? [e[1], -e[0]] : [-e[1], e[0]]); };
  const out = [];
  const push = (p) => { const q = out[out.length - 1]; if (!q || Math.hypot(q[0] - p[0], q[1] - p[1]) > 1e-6) out.push(p); };
  for (let i = 0; i < n; i++) {
    const a = pts[i], b = pts[(i + 1) % n], c = pts[(i + 2) % n];
    const n1 = nrm(a, b), n2 = nrm(b, c);
    const pA = [b[0] + n1[0] * d, b[1] + n1[1] * d], pB = [b[0] + n2[0] * d, b[1] + n2[1] * d];
    const cross = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
    if (cross * sgn > 1e-12) {  // convex: sweep an arc around the corner from n1 to n2
      const a0 = Math.atan2(n1[1], n1[0]);
      let turn = Math.atan2(n2[1], n2[0]) - a0;
      while (turn > Math.PI) turn -= 2 * Math.PI;
      while (turn < -Math.PI) turn += 2 * Math.PI;
      const steps = Math.max(1, Math.ceil(Math.abs(turn) / (Math.PI / 18)));
      for (let k = 0; k <= steps; k++) { const t = a0 + turn * k / steps; push([b[0] + Math.cos(t) * d, b[1] + Math.sin(t) * d]); }
    } else {  // concave (or straight): the two pushed edges cross — that crossing keeps the gap exact on both sides
      const e1 = [b[0] - a[0], b[1] - a[1]], e2 = [c[0] - b[0], c[1] - b[1]], den = e1[0] * e2[1] - e1[1] * e2[0];
      if (Math.abs(den) < 1e-9) push(pA);
      else { const t = ((pB[0] - pA[0]) * e2[1] - (pB[1] - pA[1]) * e2[0]) / den; push([pA[0] + e1[0] * t, pA[1] + e1[1] * t]); }
    }
  }
  if (out.length > 1 && Math.hypot(out[0][0] - out[out.length - 1][0], out[0][1] - out[out.length - 1][1]) <= 1e-6) out.pop();
  return out;
}

export function fmtIn(v, showUnit = true) {
  const neg = v < 0; v = Math.abs(v);
  let whole = Math.floor(v);
  let num = Math.round((v - whole) * 16);
  if (num === 16) { whole += 1; num = 0; }
  let den = 16;
  while (num && num % 2 === 0) { num /= 2; den /= 2; }
  const frac = num ? `${whole ? " " : ""}${num}/${den}` : "";
  const body = `${whole || !num ? whole : ""}${frac}`;
  return `${neg ? "-" : ""}${body}${showUnit ? '"' : ""}`;
}

export function rotatePieceTo(piece, newRot) {
  const before = bbox(worldPoints(piece));
  const np = { ...piece, rotation: ((newRot % 360) + 360) % 360 };
  const after = bbox(worldPoints(np));
  return { ...np, x: np.x + before.cx - after.cx, y: np.y + before.cy - after.cy };
}

export function mirrorPiece(piece) {
  const before = bbox(worldPoints(piece));
  const np = { ...piece, points: piece.points.map(([x, y]) => [-x, y]).reverse(), holes: (piece.holes || []).map((h) => h.map(([x, y]) => [-x, y]).reverse()) };
  const after = bbox(worldPoints(np));
  return { ...np, x: np.x + before.cx - after.cx, y: np.y + before.cy - after.cy };
}

export function placeAt(piece, minX, minY) {
  const rb = bbox(piece.points.map((p) => rotatePt(p, piece.rotation)));
  return { ...piece, x: minX - rb.minX, y: minY - rb.minY };
}

export function nearestSegment(pts, [px, py]) {
  let best = { idx: 0, d: Infinity, pt: pts[0] };
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    const dx = b[0] - a[0], dy = b[1] - a[1], l2 = dx * dx + dy * dy || 1;
    let t = ((px - a[0]) * dx + (py - a[1]) * dy) / l2; t = Math.max(0, Math.min(1, t));
    const q = [a[0] + t * dx, a[1] + t * dy], d = Math.hypot(px - q[0], py - q[1]);
    if (d < best.d) best = { idx: i, d, pt: q };
  }
  return best;
}

function ptSegDist([px, py], a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1], l2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((px - a[0]) * dx + (py - a[1]) * dy) / l2));
  return Math.hypot(px - a[0] - t * dx, py - a[1] - t * dy);
}

export function polyDistance(a, b) {
  let best = Infinity;
  for (let i = 0; i < a.length; i++) {
    const a1 = a[i], a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      const b1 = b[j], b2 = b[(j + 1) % b.length];
      if (segIntersect(a1, a2, b1, b2)) return 0;
      const d = Math.min(ptSegDist(a1, b1, b2), ptSegDist(a2, b1, b2), ptSegDist(b1, a1, a2), ptSegDist(b2, a1, a2));
      if (d < best) best = d;
    }
  }
  return best;
}

export const polyD = (pts) => (pts.length ? `M${pts.map((p) => `${p[0].toFixed(4)} ${p[1].toFixed(4)}`).join("L")}Z` : "");

const CORNER_DEG = 20; // same threshold the cut-time knife path uses
const ARC_SEG = 0.22; // nodes this close together that keep turning the same way are samples of a small arc, not corners
const turnDeg = (a, b, c) => {
  const x1 = b[0] - a[0], y1 = b[1] - a[1], x2 = c[0] - b[0], y2 = c[1] - b[1];
  return Math.abs((Math.atan2(x1 * y2 - y1 * x2, x1 * x2 + y1 * y2) * 180) / Math.PI);
};
const turnSigned = (a, b, c) => {
  const x1 = b[0] - a[0], y1 = b[1] - a[1], x2 = c[0] - b[0], y2 = c[1] - b[1];
  return (Math.atan2(x1 * y2 - y1 * x2, x1 * x2 + y1 * y2) * 180) / Math.PI;
};
const f4 = (v) => v.toFixed(4);

// True corners of an outline (> 20° turn that is not a sample of a small traced arc) — shared by the display path and the PPF wrap tool.
export function cornerFlags(pts) {
  const n = pts.length;
  const P = (i) => pts[((i % n) + n) % n];
  const dist = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);
  const isArcSample = (i) => {
    const t = turnSigned(P(i - 1), P(i), P(i + 1));
    if (Math.abs(t) > 50) return false; // a node turning this much is a corner — arc samples turn 20–30° each (a 0.15" hook before a 32" door bottom drew that edge bowed)
    if (dist(P(i - 1), P(i)) > ARC_SEG && dist(P(i), P(i + 1)) > ARC_SEG) return false;
    const tp = turnSigned(P(i - 2), P(i - 1), P(i)), tn = turnSigned(P(i), P(i + 1), P(i + 2));
    const same = (u) => Math.abs(u) >= 6 && Math.sign(u) === Math.sign(t);
    return (same(tp) && dist(P(i - 1), P(i)) <= ARC_SEG) || (same(tn) && dist(P(i), P(i + 1)) <= ARC_SEG);
  };
  return pts.map((_, i) => turnDeg(P(i - 1), P(i), P(i + 1)) > CORNER_DEG && !isArcSample(i));
}

// Display path: centripetal Catmull-Rom spline through the stored nodes, true corners (> 20°) stay sharp.
// Mirrors what the plotter cuts (nodes are sparse, the knife path is a smooth curve through them).
// A small traced radius arrives as a few close nodes each turning 20–30° the same way: that is an arc, drawn smooth.
export function pathD(pts) {
  const n = pts.length;
  if (n < 4) return polyD(pts);
  const P = (i) => pts[((i % n) + n) % n];
  const dist = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);
  const corner = cornerFlags(pts);
  let d = `M${f4(pts[0][0])} ${f4(pts[0][1])}`;
  for (let i = 0; i < n; i++) {
    const p0 = P(i - 1), p1 = P(i), p2 = P(i + 1), p3 = P(i + 2);
    const c1 = corner[i], c2 = corner[(i + 1) % n];
    if (c1 && c2) { d += `L${f4(p2[0])} ${f4(p2[1])}`; continue; }
    const d1 = Math.sqrt(dist(p0, p1)), d2 = Math.sqrt(dist(p1, p2)), d3 = Math.sqrt(dist(p2, p3));
    let b1, b2;
    if (c1 || d1 < 1e-6 || d2 < 1e-6) b1 = [p1[0] + (p2[0] - p1[0]) / 3, p1[1] + (p2[1] - p1[1]) / 3];
    else {
      const k = 3 * d1 * (d1 + d2);
      b1 = [(d1 * d1 * p2[0] - d2 * d2 * p0[0] + (2 * d1 * d1 + 3 * d1 * d2 + d2 * d2) * p1[0]) / k,
            (d1 * d1 * p2[1] - d2 * d2 * p0[1] + (2 * d1 * d1 + 3 * d1 * d2 + d2 * d2) * p1[1]) / k];
    }
    if (c2 || d3 < 1e-6 || d2 < 1e-6) b2 = [p2[0] - (p2[0] - p1[0]) / 3, p2[1] - (p2[1] - p1[1]) / 3];
    else {
      const k = 3 * d3 * (d3 + d2);
      b2 = [(d3 * d3 * p1[0] - d2 * d2 * p3[0] + (2 * d3 * d3 + 3 * d3 * d2 + d2 * d2) * p2[0]) / k,
            (d3 * d3 * p1[1] - d2 * d2 * p3[1] + (2 * d3 * d3 + 3 * d3 * d2 + d2 * d2) * p2[1]) / k];
    }
    // A long edge leaving a dense run of nodes (a fillet's last 0.05" step, then a 32" straight door bottom) must not inherit the
    // run's tangent scaled to its own length — that drew a visible bow on a perfectly straight stored edge. The handle at each
    // end may reach no further than the shorter neighbouring segment; evenly sampled curves (handles ≈ L/3) are untouched.
    b1 = clampHandle(p1, b1, Math.min(dist(p0, p1), dist(p1, p2)));
    b2 = clampHandle(p2, b2, Math.min(dist(p2, p3), dist(p1, p2)));
    d += `C${f4(b1[0])} ${f4(b1[1])} ${f4(b2[0])} ${f4(b2[1])} ${f4(p2[0])} ${f4(p2[1])}`;
  }
  return d + "Z";
}

function clampHandle(anchor, handle, maxLen) {
  const L = Math.hypot(handle[0] - anchor[0], handle[1] - anchor[1]);
  if (L <= maxLen || L < 1e-9) return handle;
  const f = maxLen / L;
  return [anchor[0] + (handle[0] - anchor[0]) * f, anchor[1] + (handle[1] - anchor[1]) * f];
}

// Replace vertex i with a circular arc of radius r tangent to both edges (corner fillet).
export function filletCorner(pts, i, r) {
  const n = pts.length;
  if (n < 3 || r <= 0) return pts;
  const A = pts[(i - 1 + n) % n], B = pts[i], C = pts[(i + 1) % n];
  const l1 = Math.hypot(A[0] - B[0], A[1] - B[1]), l2 = Math.hypot(C[0] - B[0], C[1] - B[1]);
  if (l1 < 1e-6 || l2 < 1e-6) return pts;
  const u1 = [(A[0] - B[0]) / l1, (A[1] - B[1]) / l1], u2 = [(C[0] - B[0]) / l2, (C[1] - B[1]) / l2];
  const cosT = Math.max(-1, Math.min(1, u1[0] * u2[0] + u1[1] * u2[1]));
  const theta = Math.acos(cosT);
  if (theta < 0.05 || Math.PI - theta < 0.05) return pts;
  let t = r / Math.tan(theta / 2);
  const tMax = 0.95 * Math.min(l1, l2);
  if (t > tMax) { t = tMax; r = t * Math.tan(theta / 2); }
  const P1 = [B[0] + u1[0] * t, B[1] + u1[1] * t], P2 = [B[0] + u2[0] * t, B[1] + u2[1] * t];
  const bis = [u1[0] + u2[0], u1[1] + u2[1]], bl = Math.hypot(bis[0], bis[1]);
  const d = r / Math.sin(theta / 2);
  const O = [B[0] + (bis[0] / bl) * d, B[1] + (bis[1] / bl) * d];
  let a1 = Math.atan2(P1[1] - O[1], P1[0] - O[0]), a2 = Math.atan2(P2[1] - O[1], P2[0] - O[0]);
  let sweep = a2 - a1;
  while (sweep > Math.PI) sweep -= 2 * Math.PI;
  while (sweep < -Math.PI) sweep += 2 * Math.PI;
  const steps = Math.max(4, Math.min(48, Math.ceil((Math.abs(sweep) * r) / 0.1)));
  const arc = [];
  for (let k = 0; k <= steps; k++) { const a = a1 + (sweep * k) / steps; arc.push([O[0] + r * Math.cos(a), O[1] + r * Math.sin(a)]); }
  return [...pts.slice(0, i), ...arc, ...pts.slice(i + 1)];
}

// Fillet every sharp corner (turn > minTurnDeg) of a polygon. `r` may be a function of the corner's interior angle (deg).
export function filletAll(pts, r, minTurnDeg = 25) {
  const turn = (a, b, c) => { const t = Math.atan2(c[1] - b[1], c[0] - b[0]) - Math.atan2(b[1] - a[1], b[0] - a[0]); return Math.abs(((((t * 180) / Math.PI) + 540) % 360) - 180); };
  const sharp = pts.map((_, i) => turn(pts[(i - 1 + pts.length) % pts.length], pts[i], pts[(i + 1) % pts.length]) > minTurnDeg);
  let out = pts.map((p, i) => ({ p, sharp: sharp[i] }));
  for (let i = out.length - 1; i >= 0; i--) {
    if (!out[i].sharp) continue;
    const before = out.length;
    const cur = out.map((o) => o.p);
    const rr = typeof r === "function" ? r(180 - turn(cur[(i - 1 + cur.length) % cur.length], cur[i], cur[(i + 1) % cur.length])) : r;
    const next = filletCorner(cur, i, rr);
    const added = next.length - before;
    out = next.map((p, k) => ({ p, sharp: k >= i && k <= i + added ? false : out[k < i ? k : k - added]?.sharp || false }));
  }
  return out.map((o) => o.p);
}

// Smallest rounding that just knocks off a sharp tip: cut back `setback` inches along each edge.
export const minimalFilletRadius = (setback = 0.25) => (interiorDeg) => Math.max(0.05, setback * Math.tan(((Math.min(interiorDeg, 150)) * Math.PI) / 360));

const signedTurnDeg = (a, b, c) => {
  const x1 = b[0] - a[0], y1 = b[1] - a[1], x2 = c[0] - b[0], y2 = c[1] - b[1];
  return (Math.atan2(x1 * y2 - y1 * x2, x1 * x2 + y1 * y2) * 180) / Math.PI;
};
const segLen = (a, b) => Math.hypot(b[0] - a[0], b[1] - a[1]);

// Walk `dist` inches along the outline from node i (dir ±1); returns the point, unit tangent (in walking order) and the node index passed.
function walk(pts, i, dir, dist) {
  const n = pts.length, P = (k) => pts[((k % n) + n) % n];
  let rem = dist, k = i;
  for (let guard = 0; guard < n; guard++) {
    const a = P(k), b = P(k + dir), L = segLen(a, b);
    if (L >= rem) {
      const f = L ? rem / L : 0;
      return { pt: [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f], tan: [(b[0] - a[0]) / (L || 1), (b[1] - a[1]) / (L || 1)], last: k };
    }
    rem -= L; k += dir;
  }
  return null;
}

// Replace the pointy tip at node i (and every node within `setback` inches of it) with one smooth curve that starts and
// ends ON the outline (setback inches before / after the tip) and is tangent to both legs there. The curve lives inside the
// triangle entry–apex–exit, so it only ever trims the very tip — the pattern keeps its shape.
export function roundTip(pts, i, setback) {
  const n = pts.length;
  const back = walk(pts, i, -1, setback), fwd = walk(pts, i, 1, setback);
  if (!back || !fwd) return pts;
  const entry = back.pt, exit = fwd.pt;
  const tIn = [-back.tan[0], -back.tan[1]], tOut = fwd.tan;
  const cross = tIn[0] * tOut[1] - tIn[1] * tOut[0];
  const C = segLen(entry, exit);
  if (C < 1e-4 || Math.abs(cross) < 1e-6) return pts;
  // virtual apex V = where the two legs meet; fall back to the real node when the legs don't converge ahead
  const ex = exit[0] - entry[0], ey = exit[1] - entry[1];
  const s1 = (ex * tOut[1] - ey * tOut[0]) / cross;
  let V = [entry[0] + tIn[0] * s1, entry[1] + tIn[1] * s1];
  if (s1 <= 0 || (exit[0] - V[0]) * tOut[0] + (exit[1] - V[1]) * tOut[1] <= 0 || s1 > 4 * setback + 0.5) V = pts[((i % n) + n) % n];
  const steps = Math.max(6, Math.ceil(C / 0.05));
  const curve = [];
  for (let k = 1; k < steps; k++) {
    const t = k / steps, u = 1 - t;
    curve.push([u * u * entry[0] + 2 * u * t * V[0] + t * t * exit[0], u * u * entry[1] + 2 * u * t * V[1] + t * t * exit[1]]);
  }
  // keep nodes after the exit segment round to the node before the entry segment
  const keep = [];
  const stop = ((back.last % n) + n) % n;
  for (let k = fwd.last + 1, g = 0; g < n; k++, g++) {
    const idx = ((k % n) + n) % n;
    if (idx === stop) break;
    keep.push(pts[idx]);
  }
  if (keep.length && segLen(keep[keep.length - 1], entry) < 1e-4) keep.pop();
  if (keep.length && segLen(keep[0], exit) < 1e-4) keep.shift();
  return [...keep, entry, ...curve, exit];
}

// Auto-round: find every pointy corner (≥ clusterDeg of same-direction bend inside a ±WIN" window — one sharp node or a
// dense run of small ones) and round each with one tangent curve of radius `r` inches. The setback along each edge
// follows from the corner angle (t = r·tan(θ/2)), so an acute frameless tip is cut back far enough to actually look
// round while a gentle bend is barely touched. Nodes that a previous fillet inserted are never re-rounded.
// Trace leftovers: a node whose two segments are both tiny and whose bend goes the opposite way to a neighbour is a
// micro-jog (a 0.1" notch at a corner), not geometry — drop it so the corner reads as one bend and can be rounded.
export function collapseMicroJogs(pts, maxSeg = 0.12) {
  let cur = pts;
  for (let pass = 0; pass < 6; pass++) {
    const n = cur.length;
    if (n < 6) return cur;
    const P = (k) => cur[((k % n) + n) % n];
    const turns = cur.map((_, i) => signedTurnDeg(P(i - 1), P(i), P(i + 1)));
    const drop = new Set();
    for (let i = 0; i < n; i++) {
      if (segLen(P(i - 1), P(i)) >= maxSeg || segLen(P(i), P(i + 1)) >= maxSeg || Math.abs(turns[i]) < 8) continue;
      const prev = turns[((i - 1) % n + n) % n], next = turns[(i + 1) % n];
      if ((Math.abs(prev) >= 8 && Math.sign(prev) !== Math.sign(turns[i])) || (Math.abs(next) >= 8 && Math.sign(next) !== Math.sign(turns[i]))) drop.add(i);
    }
    if (!drop.size) return cur;
    cur = cur.filter((_, i) => !drop.has(i));
  }
  return cur;
}

export function autoRoundTips(pts, r = 0.2, seedDeg = 4, clusterDeg = 45, maxSetback = 0.35) {
  const WIN = 0.75; // corner-detection window (independent of how much we round)
  const MAX_SETBACK = maxSetback; // a needle tip is cut back at most this far along each edge — the pattern keeps its shape
  const NEXT_CORNER_DEG = 25;
  let cur = collapseMicroJogs(pts), changed = cur !== pts;
  const done = new Set();
  for (let iter = 0; iter < 40; iter++) {
    const n = cur.length, P = (k) => cur[((k % n) + n) % n], idx = (k) => ((k % n) + n) % n;
    const turns = cur.map((_, i) => signedTurnDeg(P(i - 1), P(i), P(i + 1)));
    // same-direction nodes within ±WIN" of node i (arc distance), with the cumulative turn at each
    const cluster = (i) => {
      const sgn = Math.sign(turns[i]), nodes = [i];
      for (const dir of [-1, 1]) {
        let d = 0;
        for (let k = i + dir, g = 0; g < n / 2; k += dir, g++) {
          d += segLen(P(k - dir), P(k));
          if (d > WIN) break;
          // only real kinks join the corner: the gentle 0.5–2° nodes of a curved edge running into it are the edge, not the corner
          if (Math.sign(turns[idx(k)]) === sgn && Math.abs(turns[idx(k)]) >= Math.max(2, 0.12 * Math.abs(turns[i]))) nodes.push(idx(k));
        }
      }
      return nodes;
    };
    let best = null;
    for (let i = 0; i < n; i++) {
      if (Math.abs(turns[i]) < seedDeg || done.has(cur[i])) continue;
      const nodes = cluster(i);
      if (nodes.some((k) => done.has(cur[k]))) continue;
      const total = nodes.reduce((s, k) => s + Math.abs(turns[k]), 0);
      if (total >= clusterDeg && (!best || total > best.total)) best = { i, total, nodes };
    }
    if (!best) break;
    // apex = the node where the cumulative bend reaches half the corner's total turn (centre of a dense corner)
    const ordered = [...best.nodes].sort((a, b) => a - b);
    const wrap = ordered.length > 1 && ordered[ordered.length - 1] - ordered[0] > n / 2;
    const seq = wrap ? [...ordered.filter((k) => k > n / 2), ...ordered.filter((k) => k <= n / 2)] : ordered;
    let acc = 0, apex = best.i;
    for (const k of seq) { acc += Math.abs(turns[k]); if (acc >= best.total / 2) { apex = k; break; } }
    // never un-round: if the corner is already drawn with a radius at least as large as the target, leave it alone.
    // Judge only the corner's core (nodes bending ≥ 10 % of the sharpest one) so a gentle curve running into a sharp
    // 90° node doesn't masquerade as a big existing radius.
    const sharpest = Math.max(...best.nodes.map((k) => Math.abs(turns[k])));
    const core = seq.filter((k) => Math.abs(turns[k]) >= Math.max(3, 0.1 * sharpest));
    const span = core.length > 1 ? core.slice(1).reduce((s, k, j) => s + segLen(P(core[j]), P(k)), 0) : 0;
    const coreTurn = core.reduce((s, k) => s + Math.abs(turns[k]), 0);
    const existingR = coreTurn > 0 && core.length >= 4 ? span / ((coreTurn * Math.PI) / 180) : 0; // 1–3 nodes = a sharp or chamfered corner, never "already round"
    // "already round" also means smooth: a corner built from a few 20–30° facets is still a visible polygon, so it is redone
    if (existingR >= r * 0.9 && sharpest <= 15) { best.nodes.forEach((k) => done.add(cur[k])); continue; }
    // a chamfered or stair-stepped corner (two or three kink nodes a few tenths apart) must become ONE curve: the fillet
    // has to reach past the farthest kink on both sides, however small the chosen radius is
    const along = (from, to, dir) => { let d = 0; for (let j = from, g = 0; idx(j) !== idx(to) && g < n; j += dir, g++) d += segLen(P(j), P(j + dir)); return d; };
    const ai = core.indexOf(apex);
    const reach = core.reduce((m, k, j) => (j === ai ? m : Math.max(m, along(apex, k, j < ai ? -1 : 1))), 0);
    // chamfer / stair-step corners (kinks a few tenths apart) still become one curve, but the reach is capped so a dense
    // trace can never turn a "tiny" radius into a big round — the pattern keeps its shape
    // the default "tiny" / "xsmall" rounding only takes the point off: it never cuts further back than DigiCut's own small radius
    // would (≤ 2 × the geometric setback); the 0.2" floor that merges chamfered corners belongs to the bigger chosen radii
    const fs = filletSetback(r, best.total);
    let setback = Math.min(MAX_SETBACK, Math.max(fs, Math.min(reach + 0.02, r <= 0.15 ? 2 * fs : Math.max(0.2, 2 * fs))));
    // a chamfered corner (two or more real kinks, each a good share of the turn) half-covered by a tiny radius leaves a point
    // behind: cover the whole kink cluster when it is short, otherwise it is already a soft corner — leave it exactly as drawn
    const kinks = core.filter((k) => Math.abs(turns[k]) >= 0.25 * sharpest);
    const kinkReach = kinks.reduce((m, k) => (k === apex ? m : Math.max(m, along(apex, k, core.indexOf(k) < ai ? -1 : 1))), 0);
    if (r <= 0.15 && kinks.length > 1 && kinkReach + 0.02 > setback) {
      if (kinkReach <= 0.15) setback = Math.min(MAX_SETBACK, kinkReach + 0.02);
      else { best.nodes.forEach((k) => done.add(cur[k])); continue; }
    }
    // don't eat into the next real corner: cap the setback at 45 % of the run to the nearest sharp node outside this corner
    const inCorner = new Set(best.nodes);
    let cap = setback;
    for (const dir of [-1, 1]) {
      let d = 0;
      for (let k = apex + dir, g = 0; g < n - 1; k += dir, g++) {
        d += segLen(P(k - dir), P(k));
        if (!inCorner.has(idx(k)) && Math.abs(turns[idx(k)]) >= NEXT_CORNER_DEG) { cap = Math.min(cap, 0.45 * d); break; }
        if (d > setback * 3) break;
      }
    }
    if (cap < 0.015) { best.nodes.forEach((k) => done.add(cur[k])); continue; }  // 1/32" on an obtuse corner is a ~0.02" setback — still a real cut
    const next = roundTip(cur, apex, cap);
    if (next === cur) { best.nodes.forEach((k) => done.add(cur[k])); continue; }
    const before = new Set(cur);
    next.forEach((p) => { if (!before.has(p)) done.add(p); });
    cur = next; changed = true;
  }
  return changed ? cur : pts;
}

// Distance from the apex to each tangent point for a circular fillet of radius r on a corner that turns `turnDeg`.
export const filletSetback = (r, turnDeg) => r * Math.tan((Math.min(Math.abs(turnDeg), 170) * Math.PI) / 360);

// ---------- Corner rounding for dense traced outlines ----------
// Real templates describe a "pointy" corner with many close points, so a single-vertex fillet barely does anything.
// These helpers measure the total turn inside a short window around the apex, cut back a setback along the outline on
// both sides and replace the tip with a smooth tangent curve — the way a frameless (Tesla-style) glass corner is rounded.
const _unit = (a, b) => { const l = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1; return [(b[0] - a[0]) / l, (b[1] - a[1]) / l]; };

function _windowTurn(pts, i, halfWin) {
  const n = pts.length;
  let total = signedTurnDeg(pts[(i - 1 + n) % n], pts[i], pts[(i + 1) % n]);
  for (const dir of [-1, 1]) {
    let s = 0, k = i;
    for (let step = 0; step < n - 2; step++) {
      const nk = (k + dir + n) % n;
      s += segLen(pts[k], pts[nk]);
      if (s > halfWin) break;
      k = nk;
      total += signedTurnDeg(pts[(k - 1 + n) % n], pts[k], pts[(k + 1) % n]);
    }
  }
  return total;
}

// Corners of a closed outline: apex index + total turn (deg) inside a `window`-inch arc around it.
export function findCorners(pts, { window = 0.8, minTurnDeg = 30 } = {}) {
  const n = pts.length;
  if (n < 4) return [];
  const turns = pts.map((_, i) => _windowTurn(pts, i, window / 2));
  const cand = turns.map((t, i) => ({ i, turnDeg: t })).filter((c) => Math.abs(c.turnDeg) >= minTurnDeg).sort((a, b) => Math.abs(b.turnDeg) - Math.abs(a.turnDeg));
  const accepted = [];
  const arcBetween = (a, b) => { let s = 0; for (let k = a; k !== b; k = (k + 1) % n) s += segLen(pts[k], pts[(k + 1) % n]); return s; };
  for (const c of cand) {
    if (accepted.some((a) => Math.min(arcBetween(a.i, c.i), arcBetween(c.i, a.i)) < window * 1.25)) continue;
    accepted.push({ ...c, i: _refineApex(pts, c.i, window / 2) });
  }
  return accepted.sort((a, b) => a.i - b.i);
}

// The window sum is flat across the whole tip — the true apex is the vertex with the sharpest local kink inside it.
function _refineApex(pts, i, reach) {
  const n = pts.length;
  let best = i, bestTurn = Math.abs(_windowTurn(pts, i, 0.08));
  for (const dir of [-1, 1]) {
    let s = 0, k = i;
    while (s < reach) {
      const nk = (k + dir + n) % n; s += segLen(pts[k], pts[nk]); k = nk;
      const t = Math.abs(_windowTurn(pts, k, 0.08));
      if (t > bestTurn + 1e-9) { best = k; bestTurn = t; }
    }
  }
  return best;
}

// Snap a clicked point to the sharpest vertex within `reach` inches along the outline.
export function nearestCornerApex(pts, i, reach = 0.35, window = 0.8) {
  const n = pts.length;
  let best = i, bestTurn = Math.abs(_windowTurn(pts, i, window / 2));
  for (const dir of [-1, 1]) {
    let s = 0, k = i;
    while (s < reach) {
      const nk = (k + dir + n) % n; s += segLen(pts[k], pts[nk]); k = nk;
      const t = Math.abs(_windowTurn(pts, k, window / 2));
      if (t > bestTurn) { best = k; bestTurn = t; }
    }
  }
  return _refineApex(pts, best, window / 2);
}

// Round the corner whose apex is vertex i with (approximately) radius r. Returns the same array when i isn't a corner.
export function roundCornerAt(pts, i, r, { window = 0.8, minTurnDeg = 20 } = {}) {
  const n = pts.length;
  if (n < 6 || r <= 0) return pts;
  const turn = _windowTurn(pts, i, window / 2);
  const phi = Math.min(Math.abs(turn), 175) * Math.PI / 180;
  if (Math.abs(turn) < minTurnDeg) return pts;
  // rotate so the apex sits mid-array: no wrap-around bookkeeping
  const shift = ((i - Math.floor(n / 2)) % n + n) % n;
  const P = pts.map((_, k) => pts[(k + shift) % n]);
  const m = Math.floor(n / 2);
  const per = P.reduce((s, p, k) => s + segLen(p, P[(k + 1) % n]), 0);
  let t = filletSetback(r, turn);                       // setback along each edge for a true circular fillet (t = r·tan(φ/2))
  t = Math.min(t, per * 0.2);
  // walk back/forward `t` inches along the outline → tangent points P1 / P2 and the outline direction there
  const walk = (dir) => {
    let s = 0, k = m;
    while (true) {
      const nk = k + dir;
      if (nk < 1 || nk > n - 2) return null;
      const L = segLen(P[k], P[nk]);
      if (s + L >= t) { const f = (t - s) / L; return { idx: k, next: nk, pt: [P[k][0] + (P[nk][0] - P[k][0]) * f, P[k][1] + (P[nk][1] - P[k][1]) * f], tan: _unit(P[nk], P[k]) }; }
      s += L; k = nk;
    }
  };
  const a = walk(-1), b = walk(1);
  if (!a || !b) return pts;
  // handle length for a cubic that hugs the circular arc of turn phi through tangent points t from the virtual apex
  const h = t * (4 / 3) * Math.tan(phi / 4) / Math.tan(phi / 2);
  const P1 = a.pt, P2 = b.pt, C1 = [P1[0] + a.tan[0] * h, P1[1] + a.tan[1] * h], C2 = [P2[0] + b.tan[0] * h, P2[1] + b.tan[1] * h];
  const arcLen = Math.max(phi * r, 0.3);
  const steps = Math.max(6, Math.min(72, Math.ceil(arcLen / 0.05)));
  const curve = [];
  for (let k = 0; k <= steps; k++) {
    const u = k / steps, v = 1 - u;
    curve.push([v * v * v * P1[0] + 3 * v * v * u * C1[0] + 3 * v * u * u * C2[0] + u * u * u * P2[0], v * v * v * P1[1] + 3 * v * v * u * C1[1] + 3 * v * u * u * C2[1] + u * u * u * P2[1]]);
  }
  // keep everything up to the vertex before P1 and from the vertex after P2, drop the tip in between
  const out = [...P.slice(0, a.next + 1), ...curve, ...P.slice(b.next)];
  // rotate back so the outline still starts where it did (unless the old start point was part of the rounded tip)
  const idx0 = out.indexOf(pts[0]);
  return idx0 > 0 ? [...out.slice(idx0), ...out.slice(0, idx0)] : out;
}

// Round every detected corner of the outline with radius r.
export function roundAllCorners(pts, r, opts = {}) {
  const corners = findCorners(pts, opts);
  if (!corners.length) return pts;
  let cur = pts;
  for (const c of corners) {
    // indices move after every rounding, so re-find the apex by position
    const [ax, ay] = pts[c.i];
    let apex = 0, best = Infinity;
    cur.forEach((p, k) => { const d = Math.hypot(p[0] - ax, p[1] - ay); if (d < best) { best = d; apex = k; } });
    cur = roundCornerAt(cur, apex, r, opts);
  }
  return cur;
}

// Frameless glass (Tesla, Porsche, convertibles…): DigiCut draws the exposed top corners sharp, the glass itself is round
// (~3/8"). The film carries the glass's own radius and sits a hairline inside the edge all round — an even sliver of glass
// at the top, the side and the corner, never an overhanging tip. r = glass corner radius, m = edge margin (inches).
export function glassMatch(pts, r = 0.375, m = 1 / 32) {
  let out = r > 0 ? autoRoundTips(pts, r, 4, 45, 0.7) : pts;
  if (m > 0) out = offsetPolygon(out, -m);
  return out;
}
